<template>
  <div style="height: 260px; width: 100%">
    <Line :data="chartData" :options="options" />
  </div>
</template>

<script setup>
import { Line } from 'vue-chartjs'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  BarController,
  LineController,
  Filler
} from 'chart.js'
import { computed } from 'vue'
import { useWebSocketStore } from '@/store/websocket.store'

const store = useWebSocketStore()
const clickStats = computed(() => store.adminActivityStats || [])

ChartJS.register(
  LineController,
  BarController,
  LineElement,
  PointElement,
  BarElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler
)

/* =======================================================
   1. Все уникальные timestamp
======================================================= */

const timestamps = computed(() => {
  const set = new Set()

  clickStats.value.forEach(admin => {
    admin?.perMinute?.forEach(m => {
      set.add(m.timestamp)
    })
  })

  return Array.from(set).sort((a, b) => a - b)
})

/* =======================================================
   2. Labels
======================================================= */

const labels = computed(() =>
  timestamps.value.map(ts =>
    new Date(ts).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    })
  )
)

/* =======================================================
   3. Суммарные клики (Bar)
======================================================= */

const totalPerMinute = computed(() => {
  const map = {}

  clickStats.value.forEach(admin => {
    admin?.perMinute?.forEach(({ timestamp, count }) => {
      map[timestamp] = (map[timestamp] || 0) + count
    })
  })

  return timestamps.value.map(ts => map[ts] || 0)
})

/* =======================================================
   4. Палитра
======================================================= */

const palette = ['#3b82f6', '#22c55e', '#ef4444', '#eab308', '#a855f7', '#06b6d4']

/* =======================================================
   5. Линии админов
======================================================= */

const adminDatasets = computed(() =>
  clickStats.value.map((admin, index) => {
    const map = {}
    admin?.perMinute?.forEach(({ timestamp, count }) => {
      map[timestamp] = count
    })

    const color = palette[index % palette.length]

    return {
      type: 'line',
      label: admin.admin,
      data: timestamps.value.map(ts => map[ts] || 0),
      borderColor: color,
      backgroundColor: color + '22', // мягкая заливка
      borderWidth: 3,
      tension: 0.35,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointHitRadius: 20,
      fill: true,
      yAxisID: 'y'
    }
  })
)

/* =======================================================
   6. ChartData
======================================================= */

const chartData = computed(() => ({
  labels: labels.value,
  datasets: [
    {
      type: 'bar',
      label: 'Всего кликов',
      data: totalPerMinute.value,
      yAxisID: 'volume',
      backgroundColor: 'rgba(255,255,255,0.18)',
      borderRadius: 4,
      barThickness: 10
    },
    ...adminDatasets.value
  ]
}))

/* =======================================================
   7. Options
======================================================= */

const options = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    legend: {
      position: 'top',
      labels: {
        color: '#e5e7eb',
        boxWidth: 14,
        padding: 16
      }
    },
    tooltip: {
      backgroundColor: '#111827',
      borderColor: '#374151',
      borderWidth: 1,
      padding: 12,
      titleColor: '#fff',
      bodyColor: '#e5e7eb',
      displayColors: true
    }
  },
  scales: {
    x: {
      ticks: {
        color: '#9ca3af',
        maxRotation: 0
      },
      grid: {
        color: 'rgba(255,255,255,0.06)'
      }
    },
    y: {
      position: 'right',
      min: 0,
      ticks: {
        color: '#9ca3af',
        stepSize: 2
      },
      grid: {
        color: 'rgba(255,255,255,0.1)'
      }
    },
    volume: {
      position: 'left',
      display: false,
      min: 0
    }
  }
}
</script>
